import { useMemo } from "react";
import { useSelector } from "react-redux";

import type { Size } from "./canvasTransformMath";

export interface CanvasSize extends Size {
  scale: number;
}

/**
 * Port of v3's useGetCanvasSize: the "cover" display size of the canvas at
 * fullscreen (scale 1). We cover the current viewport so the canvas never leaves
 * gaps; the docked wrapper then shrinks this via minScale to fit an embedded slot.
 *
 * Base = the canvas resolution (canvasDimensions passed as width/height).
 */
export function useCanvasSize(baseWidth?: number, baseHeight?: number): CanvasSize {
  // Both live on serverState and are updated by the app's resize handlers
  // (setWindowWidth/setWindowHeight → SET_WINDOW_HEIGHT in serverReducer). This
  // is what makes the cover-scale flip correctly between portrait and landscape.
  const windowWidth = useSelector((s: any) => s.serverState.windowWidth);
  const windowHeight = useSelector((s: any) => s.serverState.windowHeight);

  return useMemo(() => {
    const containerWidth = windowWidth || (typeof window !== "undefined" ? window.innerWidth : 1920);
    const containerHeight = windowHeight || (typeof window !== "undefined" ? window.innerHeight : 1080);
    const bw = baseWidth || 1920;
    const bh = baseHeight || 1080;

    const scaleToWidth = containerWidth / bw;
    const scaleToHeight = containerHeight / bh;
    const scale = Math.max(scaleToWidth, scaleToHeight);

    return { width: bw * scale, height: bh * scale, scale };
  }, [windowWidth, windowHeight, baseWidth, baseHeight]);
}

export default useCanvasSize;
